import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import * as BlinkCardSDK from '@microblink/blinkcard-in-browser-sdk';
import { ErrorCodes } from '@microblink/blinkcard-in-browser-sdk';
import { BlinkCardRecognizerResult } from '@microblink/blinkcard-in-browser-sdk/src/Recognizers/BlinkCard/BlinkCardRecognizer';
import { ClaimFlowType } from 'types';

import BlinkCard from 'components/SubPages/ClaimInfo/features/BlinkCard';

import { useUserContext } from 'hooks/useUserContext';

import detectMobileScreen from 'utils/detectMobileScreen';
import {
  trackCTATapped,
  trackOcrNotTriggered,
  trackOcrRecognizerError,
  trackOcrTriggered,
} from 'utils/segment/trackingEvents';

function checkUserBrowserVersionSupport(): boolean {
  // For development on the web
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  const userAgent = window.navigator.userAgent;
  const userAgentMatchResult = userAgent.match(/(iPhone OS|Android)\s(\d.)/i);
  let isOsVersionSupported = false;
  if (userAgentMatchResult) {
    const [, os, osVersion] = userAgentMatchResult;
    isOsVersionSupported =
      os === 'Android' ? Number(osVersion) > 8 : Number(osVersion) > 13;
  }
  return isOsVersionSupported;
}

type UseBlinkCardDialogProps = {
  onShow?: () => void;
  onDismiss?: () => void;
  onError?: () => void;
};

type ModalProps = {
  onScanSuccess: (recognizer: BlinkCardRecognizerResult) => void;
};

type UseBlinkCardDialogApi = {
  Modal: FunctionComponent<ModalProps>;
  isShowing: boolean;
  handleShow: () => void;
  handleDismiss: () => void;
  errorMessage?: string;
  isOcrAvailable: boolean;
};

function useBlinkCardDialog({
  onShow,
  onDismiss,
  onError,
}: UseBlinkCardDialogProps): UseBlinkCardDialogApi {
  const [userData] = useUserContext();
  const { publicId, flipperFlags } = userData;
  const isOcrEnabled = flipperFlags.ocrEnabled;
  const isMobileScreen = detectMobileScreen();

  const isBrowserSupported = BlinkCardSDK.isBrowserSupported();
  const isOsVersionSupported = checkUserBrowserVersionSupport();
  const isCurrentUserSupported = isBrowserSupported && isOsVersionSupported;

  if (!isCurrentUserSupported) {
    const unsupportedReason = !isBrowserSupported
      ? 'web assembly support'
      : 'browser version';
    trackOcrNotTriggered(publicId, unsupportedReason);
  }

  const isOcrAvailable =
    isCurrentUserSupported && isOcrEnabled && isMobileScreen;

  const [isShowing, setIsShowing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (isShowing) {
      trackOcrTriggered(publicId);
    }
  }, [isShowing, publicId]);

  const handleShow = useCallback(() => {
    setIsShowing(true);
    if (typeof onShow === 'function') {
      onShow();
    }
  }, [onShow]);

  const handleDismiss = useCallback(() => {
    setIsShowing(false);
    if (typeof onDismiss === 'function') {
      onDismiss();
    }

    trackCTATapped(
      ClaimFlowType.pay,
      'OCR Cancel Button',
      'Claim Info Page',
      'pay_anyone_ocr_cancel_button',
      publicId
    );
  }, [onDismiss, publicId]);

  const handleError = useCallback(
    (errorMessage: string, errorCode?: string) => {
      setIsShowing(false);
      if (errorCode === ErrorCodes.WORKER_WASM_LOAD_FAILURE) {
        setErrorMessage('Sorry, there was a problem loading your camera');
      } else {
        setErrorMessage(errorMessage);
      }

      if (typeof onError === 'function') {
        onError();
      }

      trackOcrRecognizerError(publicId, errorMessage);

      datadogRum.addError(new Error('OCR Error'), {
        publicId,
        reason: errorMessage,
      });
    },
    [onError, publicId]
  );

  function Modal({ onScanSuccess }: ModalProps) {
    return (
      <BlinkCard
        onScanSuccess={onScanSuccess}
        onDismiss={handleDismiss}
        onError={handleError}
      />
    );
  }

  return {
    Modal,
    isShowing,
    handleShow,
    handleDismiss,
    errorMessage,
    isOcrAvailable,
  };
}

export default useBlinkCardDialog;
